import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { CheckCircle2, ListTodo, Calendar, Users, ArrowRight, Sparkles, Menu, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import LoginDialog from '@/components/LoginDialog';

// Feature illustration URLs
const IMAGES = {
  hero: 'https://static.prod-images.emergentagent.com/jobs/cd79566b-e126-4817-a29e-6d25afb816cf/images/b83d98a56cf493d9e29ec82b6b691bf63cf6affac95d53f89811283e8f2159a2.png',
  tags: 'https://static.prod-images.emergentagent.com/jobs/cd79566b-e126-4817-a29e-6d25afb816cf/images/fbb94fe4ded8d324105c30507c6344f6a0e885451b1d5d635d5b45ea735cc206.png',
  lists: 'https://static.prod-images.emergentagent.com/jobs/cd79566b-e126-4817-a29e-6d25afb816cf/images/89ad2fb84dd4f5c328aa64586bf5381628e62491383d5d6a3ced63b987c7c0da.png',
  schedule: 'https://static.prod-images.emergentagent.com/jobs/cd79566b-e126-4817-a29e-6d25afb816cf/images/eb0ac5415504b36977530b32b4670556962456189bef26420648f67472972ad7.png',
  team: 'https://static.prod-images.emergentagent.com/jobs/cd79566b-e126-4817-a29e-6d25afb816cf/images/8a2d2d81c94c3b694232b476b412cf94ed9e85e57cf120eeeb0114b9b171641d.png',
};

const features = [
  {
    icon: ListTodo,
    title: 'Priority Sorting Tags',
    description: 'Organize tasks with customizable tags for Type, Domain, and Energy level. Tasks automatically sort by priority so you always know what to tackle first.',
    image: IMAGES.tags,
  },
  {
    icon: CheckCircle2,
    title: 'Active & Storage Lists',
    description: 'Keep your focus sharp with separate Active and Storage lists. Active shows only what you need today; Storage holds everything else until you need it.',
    image: IMAGES.lists,
  },
  {
    icon: Calendar,
    title: 'Scheduled Activation',
    description: 'Set future activation dates for tasks. They stay in Storage until the right day, then automatically appear in your Active list when it\'s time to act.',
    image: IMAGES.schedule,
  },
  {
    icon: Users,
    title: 'Multi-User Tags',
    description: 'Perfect for couples and small teams. Assign tasks to specific people or mark them for "Any User". Filter by assignee to see only your tasks.',
    image: IMAGES.team,
  },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();
  const [showLogin, setShowLogin] = useState(false);
  const [defaultToSignup, setDefaultToSignup] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuOpen && menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const openLogin = () => {
    setDefaultToSignup(false);
    setShowLogin(true);
    setMenuOpen(false);
  };

  const openSignup = () => {
    setDefaultToSignup(true);
    setShowLogin(true);
    setMenuOpen(false);
  };

  const handleLogout = async () => {
    await logout();
    setMenuOpen(false);
  };

  const handleBackToApp = () => {
    navigate('/app');
    setMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      {/* Login Dialog */}
      <LoginDialog 
        open={showLogin} 
        onOpenChange={setShowLogin} 
        defaultToSignup={defaultToSignup}
      />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-md border-b border-slate-700/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2 flex-shrink-0">
              <Sparkles className="h-6 w-6 text-blue-400" />
              <span className="text-xl font-bold text-white">UpNext To-Do</span>
            </div>
            
            {/* Desktop buttons */}
            <div className="hidden sm:flex items-center gap-3">
              {isAuthenticated ? (
                <>
                  <Button 
                    variant="ghost" 
                    className="text-slate-300 hover:text-black hover:bg-slate-200"
                    onClick={handleLogout}
                  >
                    Log Out
                  </Button>
                  <Button 
                    className="bg-blue-600 hover:bg-blue-500 text-white hover:text-black"
                    onClick={handleBackToApp}
                  >
                    Back to App
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    variant="ghost" 
                    className="text-slate-300 hover:text-black hover:bg-slate-200"
                    onClick={openLogin}
                  >
                    Log In
                  </Button>
                  <Button 
                    className="bg-blue-600 hover:bg-blue-500 text-white hover:text-black"
                    onClick={openSignup}
                  >
                    New Account
                  </Button>
                </>
              )}
            </div>

            {/* Mobile burger menu button */}
            <div className="sm:hidden relative" ref={menuRef}>
              <button
                className="p-2 text-slate-300 hover:text-black hover:bg-slate-200 rounded-md transition-colors"
                onClick={() => setMenuOpen(!menuOpen)}
                aria-label="Toggle menu"
              >
                {menuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>

              {/* Mobile dropdown menu */}
              {menuOpen && (
                <div className="absolute right-0 top-full mt-2 w-40 bg-slate-800 border border-slate-700/50 rounded-lg shadow-lg py-2">
                  <div className="flex flex-col gap-1 px-2">
                    {isAuthenticated ? (
                      <>
                        <Button 
                          variant="ghost" 
                          className="w-full justify-start text-slate-300 bg-transparent hover:text-black hover:bg-white"
                          onClick={handleLogout}
                        >
                          Log Out
                        </Button>
                        <Button 
                          variant="ghost"
                          className="w-full justify-start text-slate-300 bg-transparent hover:text-black hover:bg-white"
                          onClick={handleBackToApp}
                        >
                          Back to App
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button 
                          variant="ghost" 
                          className="w-full justify-start text-slate-300 bg-transparent hover:text-black hover:bg-white"
                          onClick={openLogin}
                        >
                          Log In
                        </Button>
                        <Button 
                          variant="ghost"
                          className="w-full justify-start text-slate-300 bg-transparent hover:text-black hover:bg-white"
                          onClick={openSignup}
                        >
                          New Account
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                Focus on what matters{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                  today
                </span>
              </h1>
              <p className="text-lg sm:text-xl text-slate-300 mb-8 max-w-xl mx-auto lg:mx-0">
                A task manager that separates what you need to do now from everything else. 
                Stop drowning in endless lists—see only what's actionable today.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button 
                  size="lg"
                  className="bg-blue-600 hover:bg-blue-500 text-white hover:text-black text-lg px-8 py-6 rounded-xl"
                  onClick={openSignup}
                >
                  Get Started Free
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-slate-600 text-slate-200 hover:bg-slate-800 text-lg px-8 py-6 rounded-xl"
                  onClick={openLogin}
                >
                  Sign In
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-3xl blur-2xl"></div>
              <img 
                src={IMAGES.hero} 
                alt="UpNext To-Do App Interface" 
                className="relative w-full max-w-lg mx-auto rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-800/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Built for clarity, not clutter
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Every feature is designed to help you focus on what's important right now, 
              without losing track of what's coming later.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="group bg-slate-800/80 border border-slate-700/50 rounded-2xl p-6 hover:border-blue-500/50 transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/10"
              >
                <div className="flex gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-20 h-20 rounded-xl overflow-hidden bg-slate-700/50 flex items-center justify-center">
                      <img 
                        src={feature.image} 
                        alt={feature.title}
                        className="w-16 h-16 object-contain"
                      />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <feature.icon className="h-5 w-5 text-blue-400" />
                      <h3 className="text-xl font-semibold text-white">{feature.title}</h3>
                    </div>
                    <p className="text-slate-400 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-12">
            How it works
          </h2>
          
          <div className="grid sm:grid-cols-4 gap-8">
            <div className="relative">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white text-xl font-bold mx-auto mb-4">
                1
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Create Tags</h3>
              <p className="text-slate-400 text-sm">
                Set up custom tags for task type, life domain, and energy level to match your workflow.
              </p>
            </div>
            
            <div className="relative">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white text-xl font-bold mx-auto mb-4">
                2
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Add Tasks</h3>
              <p className="text-slate-400 text-sm">
                Create tasks with tags for type, domain, and energy. Set optional activation and due dates.
              </p>
            </div>
            
            <div className="relative">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white text-xl font-bold mx-auto mb-4">
                3
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Let It Organize</h3>
              <p className="text-slate-400 text-sm">
                Tasks go to Storage until their activation date. Then they automatically move to your Active list.
              </p>
            </div>
            
            <div className="relative">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white text-xl font-bold mx-auto mb-4">
                4
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Focus & Complete</h3>
              <p className="text-slate-400 text-sm">
                Work through your Active list sorted by priority. Check off tasks and watch your progress grow.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-3xl p-8 sm:p-12 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Ready to get focused?
            </h2>
            <p className="text-lg text-blue-100 mb-8 max-w-xl mx-auto">
              Join others who've simplified their task management. Free to use, no credit card required.
            </p>
            <Button 
              size="lg"
              className="bg-white text-blue-600 hover:bg-blue-50 hover:text-black text-lg px-8 py-6 rounded-xl font-semibold"
              onClick={openSignup}
            >
              Get Your Free Account
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 border-t border-slate-700/50">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-blue-400" />
            <span className="text-slate-400">UpNext To-Do</span>
          </div>
          <p className="text-slate-500 text-sm">
            Built for focus. Designed for clarity.
          </p>
        </div>
      </footer>
    </div>
  );
}
